import { useEffect, useRef, useState } from "react";
import { BrainCircuit, Send, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const inputCls = "h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30 w-full";

export default function AdminAI() {
  const [insights, setInsights] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [insBusy, setInsBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const loadInsights = async () => {
    setInsBusy(true);
    try {
      const { data } = await api.get("/admin/ai/insights");
      setInsights(data.insights);
      setSnapshot(data.snapshot);
    } catch { toast.error("AI analizi alınamadı"); }
    finally { setInsBusy(false); }
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    const history = messages.map((m) => ({ role: m.role, text: m.text }));
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ai/chat", { message: msg, history });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch { setMessages((m) => [...m, { role: "assistant", text: "Üzgünüm, şu an yanıt veremiyorum. Lütfen tekrar deneyin." }]); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-ai-page" className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl sm:text-3xl text-forest flex items-center gap-3"><BrainCircuit className="w-7 h-7 text-gold-dark" /> AI Yönetim Asistanı</h1>
        <p className="text-stone-500 text-sm mt-1">Satış, stok, finans ve operasyon verilerinizi analiz eden yapay zekâ destekli yönetim yardımcısı.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-stone-900">Günlük Yönetim Analizi</h2>
            <button data-testid="ai-insights-button" onClick={loadInsights} disabled={insBusy} className="flex items-center gap-2 bg-gold hover:bg-gold-dark disabled:opacity-50 text-stone-900 text-xs font-bold px-4 py-2.5 rounded-xl transition-colors">
              <Sparkles className="w-4 h-4" /> {insBusy ? "Analiz ediliyor..." : insights ? "Yeniden Analiz Et" : "Analiz Oluştur"}
            </button>
          </div>
          {insights ? (
            <div data-testid="ai-insights-result" className="text-sm text-stone-700 leading-relaxed whitespace-pre-line bg-cream-dark/50 rounded-xl p-4">{insights}</div>
          ) : (
            <p className="text-sm text-stone-400 py-6 text-center">Güncel satış, stok ve finans verilerinizi analiz etmek için "Analiz Oluştur"a tıklayın.</p>
          )}
          {snapshot && (
            <div className="grid grid-cols-2 gap-2 mt-4" data-testid="ai-snapshot">
              <div className="bg-stone-50 rounded-xl p-3"><p className="text-[11px] text-stone-400">Bu Ay Ciro</p><p className="font-bold text-forest">{Number(snapshot.bu_ay_ciro_tl).toLocaleString("tr-TR")} ₺</p></div>
              <div className="bg-stone-50 rounded-xl p-3"><p className="text-[11px] text-stone-400">Bu Ay Sipariş</p><p className="font-bold text-forest">{snapshot.bu_ay_siparis_adedi}</p></div>
              <div className="bg-stone-50 rounded-xl p-3"><p className="text-[11px] text-stone-400">Kritik Stok</p><p className="font-bold text-amber-700">{snapshot.kritik_stok_urunler?.length || 0} ürün</p></div>
              <div className="bg-stone-50 rounded-xl p-3"><p className="text-[11px] text-stone-400">Stok Bildirimi Bekleyen</p><p className="font-bold text-gold-dark">{snapshot.stok_bildirimi_bekleyen_musteri}</p></div>
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl border border-stone-200/80 shadow-sm flex flex-col">
          <h2 className="font-semibold text-stone-900 px-6 pt-6">Yönetime Sor</h2>
          <p className="text-xs text-stone-400 px-6 mt-1">Örn: "Bu ay cirom nasıl?", "Hangi ürünlerin stoğu kritik?", "Kasa durumum ne?"</p>
          <div data-testid="ai-chat-messages" className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-[260px] max-h-[420px]">
            {messages.length === 0 && <p className="text-sm text-stone-400 text-center py-10">İşletmenizle ilgili bir soru sorun.</p>}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-line ${m.role === "user" ? "bg-forest text-white" : "bg-stone-100 text-stone-800"}`}>{m.text}</div>
              </div>
            ))}
            {busy && <p className="text-xs text-stone-400">Asistan yazıyor...</p>}
            <div ref={bottomRef} />
          </div>
          <div className="flex gap-2 p-4 border-t border-stone-100">
            <input data-testid="ai-chat-input" className={inputCls} placeholder="Sorunuzu yazın..." value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
            <button data-testid="ai-chat-send" onClick={send} disabled={busy || !input.trim()} className="bg-forest hover:bg-forest-dark disabled:bg-stone-300 text-white px-4 rounded-xl transition-colors shrink-0"><Send className="w-4 h-4" /></button>
          </div>
        </section>
      </div>
    </div>
  );
}
